import type {
  DataverseBot,
  DataverseTool,
  McpCredential,
} from "../integrations/resources/copilot-studio/types.ts";
import { isa } from "./is.ts";

/**
 * The set of AI platforms the inventory UI can render. `bedrock` is reserved
 * for a future integration so the union does not require edits when it ships.
 */
export const AI_PLATFORMS = [
  "copilot-studio",
  "bedrock",
  "local-agents",
] as const;

export type AIPlatform = (typeof AI_PLATFORMS)[number];

export const isAIPlatform = (value: string): value is AIPlatform =>
  isa(AI_PLATFORMS, value);

/**
 * Platform-discriminated agent. Each branch carries the raw backend shape so
 * platform-specific renderers can cast to it without intermediate mapping.
 *
 * NOTE: `AI_PLATFORMS` includes `bedrock`, but its branch is not yet in this
 * union — it will be added when that integration ships. Exhaustive switch
 * statements on `agent.platform` must therefore treat a `bedrock` narrowing
 * as dead code today. `local-agents` covers all MCP clients (Claude Code,
 * Cursor, etc.), discriminated by `data.clientPlatform`.
 */
export type McpClientAgentData = {
  readonly id: string;
  readonly clientPlatform: string;
  readonly hostname?: string;
  readonly version?: string;
  readonly redirectUri?: string;
  readonly createdAt?: number;
  readonly createdByUser?: string;
};

export type AIAgent =
  | {
      readonly platform: "copilot-studio";
      readonly data: DataverseBot;
      readonly environmentId: string;
      readonly environmentLabel: string;
    }
  | {
      readonly platform: "local-agents";
      readonly data: McpClientAgentData;
      readonly environmentId: string;
      readonly environmentLabel: string;
    };

/**
 * A downstream MCP server observed in a local agent's (Claude Code, Cursor,
 * etc.) traffic, projected into connector shape so it can flow through the
 * same managed/unmanaged summary and remediation UI as a Copilot Studio
 * connector. `managed` is true when the server id matches an entry in the
 * tenant's P0-registered MCP server registry (i.e. the call was brokered by
 * the P0 gateway); false when the agent is talking to it directly.
 */
export type LocalAgentServerConnection = {
  readonly id: string;
  readonly agentId: string;
  readonly label: string;
  readonly managed: boolean;
};

/**
 * Platform-discriminated connector — represents the downstream MCP server
 * an agent talks to (e.g. Postgres, GitHub, Salesforce), NOT the MCP client
 * configuration. In Copilot Studio this is a `connectionreference` record.
 * The client (client_id/client_secret pairing used to authenticate to the P0
 * MCP server) can be reused across multiple agents and is intentionally not
 * surfaced in the inventory UI in v1.
 *
 * NOTE: `AI_PLATFORMS` includes `bedrock`, but its branch is not yet in this
 * union — it will be added when that integration grows connectors.
 * Exhaustive switch statements on `connector.platform` must therefore treat
 * a `bedrock` narrowing as dead code today.
 */
export type AIConnector =
  | {
      readonly platform: "copilot-studio";
      readonly data: DataverseTool;
      readonly credentialType?: McpCredential;
    }
  | {
      readonly platform: "local-agents";
      readonly data: LocalAgentServerConnection;
    };

export const getAgentId = (agent: AIAgent): string => {
  switch (agent.platform) {
    case "copilot-studio":
      return agent.data.botid;
    case "local-agents":
      return agent.data.id;
  }
};

export const getAgentName = (agent: AIAgent): string => {
  switch (agent.platform) {
    case "copilot-studio":
      return agent.data.name;
    case "local-agents":
      return agent.data.hostname ?? agent.data.id;
  }
};

export const getAgentOwnerEmail = (agent: AIAgent): string | undefined => {
  switch (agent.platform) {
    case "copilot-studio":
      return agent.data.owninguser?.internalemailaddress;
    case "local-agents":
      return agent.data.createdByUser;
  }
};

export const getConnectorId = (connector: AIConnector): string => {
  switch (connector.platform) {
    case "copilot-studio":
      return connector.data.connectionreferenceid;
    case "local-agents":
      return connector.data.id;
  }
};

export const getConnectorDisplayName = (connector: AIConnector): string => {
  switch (connector.platform) {
    case "copilot-studio":
      return connector.data.displayName;
    case "local-agents":
      return connector.data.label;
  }
};

export const getConnectorAgentId = (connector: AIConnector): string => {
  switch (connector.platform) {
    case "copilot-studio":
      return connector.data.agentId;
    case "local-agents":
      return connector.data.agentId;
  }
};

export const PLATFORM_LABEL: Record<AIPlatform, string> = {
  "copilot-studio": "Copilot Studio",
  bedrock: "Bedrock",
  "local-agents": "Local agents",
};

export const getAgentPlatformLabel = (agent: AIAgent): string =>
  PLATFORM_LABEL[agent.platform];

/**
 * Matches the url-encoded marker Power Platform (and other hosts) inject into
 * a connector/connection identifier when the connector routes through the P0
 * MCP gateway. Accepts both legacy `p0-20mcp-20server` (a single "p0 mcp
 * server" custom connector) and the per-integration form
 * `p0-20<something>-20mcp-20server` (e.g. "p0 Github mcp server").
 */
const MANAGED_MCP_CONNECTOR_PATTERN = /p0-20(?:[^\s]+?-20)?mcp-20server/i;

export const isManagedMcpConnectorId = (
  connectorId: string | null | undefined
): boolean => !!connectorId && MANAGED_MCP_CONNECTOR_PATTERN.test(connectorId);
